// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import usePageSize from "@/hooks/usePageSize";
import useBulk from "@/hooks/useBulk";
import usePopup from "@/hooks/usePopup";
import useSelection from "@/hooks/useSelection";
import useTranslation from "@/hooks/useTranslation";
import type { TyCustomer } from "@/types";
import CustomerCard from "@/UI/cards/CustomerCard";
import ActionsBar from "@/UI/components/ActionsBar";
import BulkBar from "@/UI/components/BulkBar";
import type { TyBulkField } from "@/UI/components/BulkBar/types";
import CrmStats from "@/UI/components/CrmStats";
import EmptyState from "@/UI/components/EmptyState";
import Paginator from "@/UI/components/Paginator";
import FilterSearchInput from "@/UI/inputs/FilterSearchInput";
import SelectInput from "@/UI/inputs/SelectInput";
import ActionsPopup, { type TyAction } from "@/UI/popups/ActionsPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import CustomerFormPopup from "@/UI/popups/CustomerFormPopup";
import InteractionFormPopup from "@/UI/popups/InteractionFormPopup";
import LeadFormPopup from "@/UI/popups/LeadFormPopup";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
/**
 * The people, and the CRM's front door. The section is called the customers and an enquiry is
 * something one of them has, so this is what opens when the sidebar is pressed.
 *
 * It is read when somebody rings — so it searches the three things a caller can give you, each in
 * its own box, and every row carries the live enquiry to act on before you pick up.
 */
export default function CustomersScreen() {
    const t = useTranslation();
    const navigate = useNavigate();
    const addComp = usePopup((state) => state.addComp);
    const [params, setParams] = useSearchParams();
    const pageSize = usePageSize((state) => state.pageSize);

    const search = params.get("search") ?? "";
    const byName = params.get("name") ?? "";
    const byPhone = params.get("phone") ?? "";
    const byEmail = params.get("email") ?? "";
    const status = params.get("status") ?? "";
    const blocked = params.get("isBlocked") ?? "";
    const page = Math.max(1, Number(params.get("page")) || 1);

    const [isFiltering, setIsFiltering] = useState(false);
    const [draftSearch, setDraftSearch] = useState(search);
    const [draftName, setDraftName] = useState(byName);
    const [draftPhone, setDraftPhone] = useState(byPhone);
    const [draftEmail, setDraftEmail] = useState(byEmail);
    const [draftStatus, setDraftStatus] = useState(status);
    const [draftBlocked, setDraftBlocked] = useState(blocked);

    const isFiltered = !!search || !!byName || !!byPhone || !!byEmail || !!status || !!blocked;

    const customers = useFetch<{ customers: TyCustomer[]; total: number }>("/crm/customers", {}, false);
    const action = useFetch("/crm/customers", {}, false);
    const selection = useSelection();

    const load = () => {
        const query: Record<string, unknown> = { limit: pageSize, page };
        if (search.trim()) query.search = search.trim();
        if (byName.trim()) query.name = byName.trim();
        if (byPhone.trim()) query.phone = byPhone.trim();
        if (byEmail.trim()) query.email = byEmail.trim();
        if (status) query.status = status;
        if (blocked) query.isBlocked = blocked;
        customers.invoke({ query });
    };

    const bulk = useBulk({ path: "/crm/customers", selection, onDone: load });

    useEffect(() => {
        load();
        // A narrowed or paged list is a different list, so the picks made on the old one go with it.
        selection.clear();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, byName, byPhone, byEmail, status, blocked, page, pageSize]);

    useEffect(() => {
        if (!action.succeededAt) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action.succeededAt]);

    const apply = (next: Record<string, string | null>) => {
        const merged = new URLSearchParams(params);
        for (const [key, value] of Object.entries(next)) {
            if (value) merged.set(key, value);
            else merged.delete(key);
        }
        if (!("page" in next)) merged.delete("page");
        setParams(merged, { replace: true });
    };

    const openForm = (customer?: TyCustomer) =>
        addComp({
            // Centred: five fields is the wrong shape for a full-height panel.
            name: "customer-form",
            Component: CustomerFormPopup,
            anim: "scale",
            details: { customer: customer ?? null, onDone: load },
        });

    const openActions = (customer: TyCustomer, anchor: DOMRect) => {
        // No "open" entry: the whole card already does that, and offering it again took the first
        // row of every menu to repeat the one thing the card is for.
        const actions: TyAction[] = [
            { key: "edit", label: t.common.edit, icon: "edit", onSelect: () => openForm(customer) },
            /* How a selection starts. Adding to it afterwards is a press on the card itself. */
            { key: "select", label: t.bulk.select, icon: "check", onSelect: () => selection.toggle(customer._id) },
            {
                key: "block",
                label: customer.isBlocked ? t.crm.unblock : t.crm.block,
                icon: "clear",
                // Confirmed like a delete: it is silent to the person it is done to, so the only
                // chance anybody has to think about it is before it happens.
                onSelect: () =>
                    addComp({
                        name: "confirm",
                        Component: ConfirmPopup,
                        anim: "scale",
                        details: {
                            title: customer.isBlocked ? t.crm.unblock : t.crm.block,
                            message: (customer.isBlocked ? t.crm.confirmUnblock : t.crm.confirmBlock).replace("{name}", customer.name),
                            confirmLabel: customer.isBlocked ? t.crm.unblock : t.crm.block,
                            onConfirm: () =>
                                action.invoke({ method: "PATCH", path: `/crm/customers/${customer._id}`, data: { isBlocked: !customer.isBlocked } }),
                        },
                    }),
            },
            {
                key: "remove",
                label: t.common.remove,
                icon: "trash",
                danger: true,
                onSelect: () =>
                    addComp({
                        name: "confirm",
                        Component: ConfirmPopup,
                        anim: "scale",
                        details: {
                            title: t.common.remove,
                            message: t.crm.confirmRemoveCustomer.replace("{name}", customer.name),
                            confirmLabel: t.common.remove,
                            onConfirm: () => action.invoke({ method: "DELETE", path: `/crm/customers/${customer._id}` }),
                        },
                    }),
            },
        ];

        addComp({
            name: "customer-actions",
            Component: ActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            details: { actions },
            anchor: { top: anchor.top, left: anchor.left, right: anchor.right, bottom: anchor.bottom, width: anchor.width, height: anchor.height },
        });
    };

    /**
     * Two fields. **A name, a phone, an email and a national id are refused by the API** — each one
     * *is* the person, and the phone most of all, since it is the identity and it is unique, so a
     * bulk write would collide with itself. See `ai.plans/bulk-edit-feature.md` §2d.
     */
    const bulkFields: TyBulkField[] = [
        {
            key: "status",
            label: t.crm.fields.status,
            kind: "select",
            options: constants.customerStatuses.map((item) => ({ value: item, label: t.crm.statuses[item] })),
        },
        {
            key: "isBlocked",
            label: t.crm.blocked,
            kind: "check",
            options: [
                { value: "1", label: t.crm.blocked },
                { value: "0", label: t.crm.notBlocked },
            ],
        },
    ];

    return (
        <section className="customers-screen">
            {/* Above the bar, and the same four on the other tab: the section's state does not
                change because you walked from the people to their enquiries. */}
            <CrmStats reloadAt={action.succeededAt} />

            <ActionsBar
                tabs={[
                    { key: "customers", label: t.crm.customers },
                    { key: "leads", label: t.crm.leads },
                    { key: "installments", label: t.sales.installments },
                ]}
                activeTab="customers"
                onTab={(key) => key !== "customers" && navigate(key === "leads" ? "/crm/leads" : "/crm/installments")}
                addLabel={t.common.add}
                onAdd={() => openForm()}
                isFilterOpen={isFiltering}
                onFilterOpen={() => {
                    setDraftSearch(search);
                    setDraftName(byName);
                    setDraftPhone(byPhone);
                    setDraftEmail(byEmail);
                    setDraftStatus(status);
                    setDraftBlocked(blocked);
                    setIsFiltering(true);
                }}
                hasFilters={isFiltered}
                onFilterApply={() => {
                    setIsFiltering(false);
                    apply({
                        search: draftSearch.trim() || null,
                        name: draftName.trim() || null,
                        phone: draftPhone.trim() || null,
                        email: draftEmail.trim() || null,
                        status: draftStatus || null,
                        isBlocked: draftBlocked || null,
                    });
                }}
                onFilterClear={() => {
                    setDraftSearch("");
                    setDraftName("");
                    setDraftPhone("");
                    setDraftEmail("");
                    setDraftStatus("");
                    setDraftBlocked("");
                    setIsFiltering(false);
                    apply({ search: null, name: null, phone: null, email: null, status: null, isBlocked: null });
                }}
                onFilterCancel={() => setIsFiltering(false)}
                filterContent={
                    <>
                        {/* One box for anything, then one per field — "Ahmed on an 05 number" is a
                            question a single combined search cannot ask. */}
                        <FilterSearchInput
                            value={draftSearch}
                            onChange={setDraftSearch}
                            label={t.common.search}
                            placeholder={t.crm.searchCustomers}
                        />
                        <FilterSearchInput value={draftName} onChange={setDraftName} label={t.crm.fields.name} />
                        <FilterSearchInput value={draftPhone} onChange={setDraftPhone} label={t.crm.fields.phone} />
                        <FilterSearchInput value={draftEmail} onChange={setDraftEmail} label={t.crm.fields.email} />
                        <SelectInput
                            compact
                            label={t.crm.fields.status}
                            value={draftStatus}
                            onChange={setDraftStatus}
                            options={[
                                { value: "", label: t.crm.fields.status },
                                ...constants.customerStatuses.map((item) => ({ value: item, label: t.crm.statuses[item] })),
                            ]}
                        />
                        <SelectInput
                            compact
                            label={t.crm.blocked}
                            value={draftBlocked}
                            onChange={setDraftBlocked}
                            options={[
                                { value: "", label: t.crm.due.all },
                                { value: "1", label: t.crm.blocked },
                                { value: "0", label: t.crm.notBlocked },
                            ]}
                        />
                    </>
                }
            />

            {action.error && <p className="customers-screen__state customers-screen__state--error">{action.error}</p>}
            {customers.loading && <p className="customers-screen__state">{t.common.loading}</p>}
            {customers.error && <p className="customers-screen__state customers-screen__state--error">{customers.error}</p>}
            {!customers.loading && !customers.error && !customers.data?.customers.length && (
                <EmptyState icon="team" title={t.crm.emptyCustomers} actionLabel={t.crm.addCustomer} onAction={() => openForm()} />
            )}

            <div className="customers-screen__grid">
                {customers.data?.customers.map((customer) => (
                    <CustomerCard
                        key={customer._id}
                        customer={customer}
                        isSelected={selection.has(customer._id)}
                        isPicking={selection.isSelecting}
                        // While the bar is up the card picks rather than opens — the gesture every
                        // file manager already taught people.
                        onOpen={() => (selection.isSelecting ? selection.toggle(customer._id) : navigate(`/crm/customers/${customer._id}`))}
                        onContact={(lead) =>
                            addComp({
                                name: "interaction-form",
                                Component: InteractionFormPopup,
                                anim: "scale",
                                details: { leadId: lead._id, onDone: load },
                            })
                        }
                        onNewInterest={() =>
                            addComp({
                                name: "lead-form",
                                Component: LeadFormPopup,
                                place: "end",
                                anim: "endSlide",
                                details: { lead: null, customer, onDone: load },
                            })
                        }
                        onActions={(anchor) => openActions(customer, anchor)}
                    />
                ))}
            </div>

            {/* One or the other, never both — `hooks/useSelection` says why the pager goes away. */}
            {selection.isSelecting ? (
                <BulkBar
                    count={selection.count}
                    fields={bulkFields}
                    busy={bulk.busy}
                    note={bulk.note}
                    onClear={selection.clear}
                    onApply={(set) => bulk.apply(set)}
                    onDelete={() =>
                        bulk.confirmed({
                            title: t.common.remove,
                            message: t.bulk.confirmRemove.replace("{count}", String(selection.count)),
                            confirmLabel: t.common.remove,
                            doneLabel: t.bulk.removed,
                        })
                    }
                />
            ) : (
                <Paginator
                    page={page}
                    total={customers.data?.total ?? 0}
                    count={customers.data?.customers.length ?? 0}
                    onPage={(next) => apply({ page: next === 1 ? null : String(next) })}
                />
            )}
        </section>
    );
}
